const bindingKey = 'terminal.notification-browser';
export function browserNotificationIssue(): string | null {
  if (!window.isSecureContext) return 'Browser notifications require HTTPS or localhost.';
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window))
    return 'This browser does not support push notifications.';
  return null;
}
export function browserPermission(): NotificationPermission | 'unsupported' {
  return browserNotificationIssue() ? 'unsupported' : Notification.permission;
}
export function readBrowserBinding(userId: string): string | null {
  try {
    const stored = JSON.parse(localStorage.getItem(bindingKey) || 'null');
    return stored?.userId === userId && typeof stored.subscriptionId === 'string'
      ? stored.subscriptionId
      : null;
  } catch {
    return null;
  }
}
export function writeBrowserBinding(userId: string, subscriptionId: string) {
  try {
    localStorage.setItem(bindingKey, JSON.stringify({ userId, subscriptionId }));
  } catch {
    /* Registration still works without storage. */
  }
}
export function clearBrowserBinding(userId: string) {
  try {
    if (readBrowserBinding(userId)) localStorage.removeItem(bindingKey);
  } catch {
    /* Storage may be disabled. */
  }
}
export async function existingBrowserSubscription(): Promise<PushSubscription | null> {
  if (browserNotificationIssue()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const worker = registration?.active || registration?.waiting || registration?.installing;
  if (worker?.scriptURL !== new URL('/sw.js', location.origin).href) return null;
  return registration!.pushManager.getSubscription();
}
export async function enableBrowserPush(publicKey: string, isCurrent: () => boolean) {
  const issue = browserNotificationIssue();
  if (issue) throw new Error(issue);
  const check = () => {
    if (!isCurrent())
      throw new Error('Your account changed. Enable notifications again after signing in.');
  };
  check();
  const permission = await Notification.requestPermission();
  check();
  if (permission !== 'granted')
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked. Change this site’s notification permission in your browser settings.'
        : 'Notification permission was not granted. You can try again when ready.',
    );
  const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  check();
  const ready = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error('The notification service could not start. Please try again.')),
        15000,
      ),
    ),
  ]);
  check();
  if (ready.scope !== registration.scope)
    throw new Error('The notification service is unavailable.');
  const previous = await registration.pushManager.getSubscription();
  check();
  if (previous && !(await previous.unsubscribe()))
    throw new Error('Could not replace the previous browser registration. Please try again.');
  check();
  const raw = atob(
    publicKey
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(publicKey.length / 4) * 4, '='),
  );
  const key = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index++) key[index] = raw.charCodeAt(index);
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
  if (!isCurrent()) {
    await subscription.unsubscribe().catch(() => false);
    check();
  }
  const serialized = subscription.toJSON();
  if (!serialized.keys?.p256dh || !serialized.keys.auth) {
    await subscription.unsubscribe().catch(() => false);
    throw new Error('This browser did not provide valid notification keys.');
  }
  return {
    subscription,
    payload: {
      endpoint: subscription.endpoint,
      keys: { p256dh: serialized.keys.p256dh, auth: serialized.keys.auth },
    },
  };
}
